import { useState, useEffect } from 'react'
import type { DeliveryTypes } from 'src/components/restructure/common/FreightSimulation/FreightSimulation'
import { TruckShippingIcon } from 'src/components/Icons/TruckShippingIcon'
import { ShieldCheck } from 'src/components/Icons/ShieldCheck'

import { useProductPageContext } from '../../contexts'

type ShippingEstimateUnit =
  | 'minute'
  | 'hour'
  | 'business-day'
  | 'day'
  | 'week'
  | 'month'
  | null

function isDeliveryBeforeLimit(
  bestDeliveryDays: number,
  limitDateStr: string
): boolean {
  if (
    typeof bestDeliveryDays !== 'number' ||
    !Number.isFinite(bestDeliveryDays) ||
    bestDeliveryDays <= 0
  ) {
    return false
  }

  const today = new Date()
  const estimatedDelivery = new Date(
    today.getTime() + bestDeliveryDays * 24 * 60 * 60 * 1000
  )

  const limitDate = parseBRDate(limitDateStr)

  return estimatedDelivery <= limitDate
}

function parseBRDate(dateStr: string): Date {
  const [day, month, year] = dateStr.split('/').map(Number)

  return new Date(year, month - 1, day)
}

function getShippingEstimateValue(estimate: string): number {
  if (!estimate) return Infinity
  const match = estimate.match(/(\d+)/)

  return match ? parseInt(match[1], 10) : Infinity
}

function getShippingEstimateUnit(estimate: string): ShippingEstimateUnit {
  const lower = estimate.toLowerCase()

  if (lower.includes('min') || lower.includes('minuto')) return 'minute'

  if (
    lower.includes('bd') ||
    lower.includes('dia util') ||
    lower.includes('dias uteis') ||
    lower.includes('dia útil') ||
    lower.includes('dias úteis')
  ) {
    return 'business-day'
  }

  if (lower.includes('h') || lower.includes('hora')) return 'hour'

  if (lower.includes('d') || lower.includes('dia')) return 'day'

  if (lower.includes('w') || lower.includes('semana')) return 'week'

  if (lower.includes('m') || lower.includes('mes') || lower.includes('mês')) {
    return 'month'
  }

  return null
}

function addBusinessDays(date: Date, businessDays: number): Date {
  const result = new Date(date)
  let remainingDays = businessDays

  while (remainingDays > 0) {
    result.setDate(result.getDate() + 1)
    const dayOfWeek = result.getDay()

    // 0 = Domingo, 6 = Sábado
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      remainingDays -= 1
    }
  }

  return result
}

function shippingEstimateToDays(estimate: string): number {
  const value = getShippingEstimateValue(estimate)
  const unit = getShippingEstimateUnit(estimate)

  if (!Number.isFinite(value) || !unit) return Infinity

  switch (unit) {
    case 'minute':
      return value / (24 * 60)

    case 'hour':
      return value / 24

    case 'business-day': {
      const today = new Date()

      today.setHours(0, 0, 0, 0)
      const deliveryDate = addBusinessDays(today, value)
      const diffInMs = deliveryDate.getTime() - today.getTime()

      return Math.ceil(diffInMs / (24 * 60 * 60 * 1000))
    }

    case 'day':
      return value

    case 'week':
      return value * 7

    case 'month':
      return value * 30

    default:
      return Infinity
  }
}

function getShippingEstimate(deliveries: DeliveryTypes) {
  if (!deliveries || deliveries.length === 0) {
    return null
  }

  const newDeliveryArr = [...deliveries]

  const sortedDeliveries = newDeliveryArr.sort((a, b) => {
    const aDays = shippingEstimateToDays(a.shippingEstimate)
    const bDays = shippingEstimateToDays(b.shippingEstimate)

    return aDays - bDays
  })

  const [bestDelivery] = sortedDeliveries

  const bestDeliveryDays = bestDelivery
    ? shippingEstimateToDays(bestDelivery.shippingEstimate)
    : null

  return { bestDelivery, bestDeliveryDays }
}

export function FlagGuaranteedDelivery() {
  const {
    deliveryIsAvailable,
    isLoadingFreightSimulation,
    freightSimulationValue,
  } = useProductPageContext()

  const [eventDeliveryInfo, setEventDeliveryInfo] = useState<{
    eventName?: string
    deadline?: string
  }>({})

  useEffect(() => {
    setEventDeliveryInfo(window.dynamicYieldUtils?.eventDeliveryInfo ?? {})
  }, [])

  const { eventName = '', deadline = '' } = eventDeliveryInfo

  const isFreightAvailable =
    deliveryIsAvailable &&
    !isLoadingFreightSimulation &&
    freightSimulationValue &&
    eventName &&
    deadline

  if (!isFreightAvailable) return null

  const deliveryTypes =
    freightSimulationValue?.packages?.[0]?.deliveryTypes ?? []

  const { bestDeliveryDays } = getShippingEstimate(deliveryTypes) ?? {}

  const canDeliverInTime =
    typeof bestDeliveryDays === 'number' &&
    isDeliveryBeforeLimit(bestDeliveryDays, deadline)

  if (!canDeliverInTime) return null

  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <div className="flex items-center gap-2 bg-mint p-1.5">
        <TruckShippingIcon color="#101010" />
        <span className="text-font-size-2 font-medium text-restructure-primary">
          Receba antes do {eventName}
        </span>
      </div>
      <div className="flex items-center gap-2 bg-[#e0e1f9] p-1.5 ">
        <ShieldCheck size={16} color="#101010" />
        <span className="text-font-size-2 font-medium text-restructure-primary">
          Entrega garantida
        </span>
      </div>
    </div>
  )
}
